import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { formatTimeAgo } from "@redotech/util/date-format";
import { useMemo } from "react";

export interface UseSupportTicketCardProps {
  conversation: SupportTicketListItemResponse;
}

export interface UseSupportTicketCardResult {
  channels: readonly MessageChannel[];
  isUnread: boolean;
  timeSinceMessage: string;
  lastMessageDate: Date | null;
}

export function isSupportTicketUnread(
  conversation: SupportTicketListItemResponse,
): boolean {
  return (
    conversation.lastCustomerMessageAt !== null &&
    (conversation.readAt === null ||
      conversation.lastCustomerMessageAt > conversation.readAt)
  );
}

export function useSupportTicketCard({
  conversation,
}: UseSupportTicketCardProps): UseSupportTicketCardResult {
  const { isUnread, timeSinceMessage, lastMessageDate } = useMemo(() => {
    const isUnread = isSupportTicketUnread(conversation);

    // Use the most recent message time (customer or merchant)
    const lastMessageDate =
      getMoreRecent(
        conversation.lastCustomerMessageAt,
        conversation.lastMerchantMessageAt,
      ) ?? null;

    const timeSinceMessage = lastMessageDate
      ? formatTimeAgo(lastMessageDate.toISOString())
      : "";

    return { isUnread, timeSinceMessage, lastMessageDate };
  }, [conversation]);

  return {
    channels: conversation.channels,
    isUnread,
    timeSinceMessage,
    lastMessageDate,
  };
}

function getMoreRecent(a: Date | null, b: Date | null): Date | null {
  if (a === null) {
    return b;
  }
  if (b === null) {
    return a;
  }
  return a > b ? a : b;
}
